import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { Users, AlertTriangle, ShieldCheck, HelpCircle, Loader2 } from "lucide-react";
import TaxDisclaimer from "@/components/tax/TaxDisclaimer";

const VERDICT_META = {
  safe: { color: "#34D399", icon: ShieldCheck, label: "Under 80%" },
  watch: { color: "#FBBF24", icon: AlertTriangle, label: "80/20 threshold" },
  at_risk: { color: "#F87171", icon: AlertTriangle, label: "PSI likely" },
  no_data: { color: "#6B7280", icon: HelpCircle, label: "No data" },
};

function money(n) {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/**
 * PSI (Personal Services Income) 80/20 check. Uses invoices as primary income
 * signal, falls back to job.budget aggregation when no invoices are logged.
 */
export default function PsiCheckCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: res } = await api.get("/tax/psi-check");
      setData(res);
    } catch (err) {
      devLog.warn("psi-check failed", err);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const meta = data ? VERDICT_META[data.verdict] || VERDICT_META.no_data : null;
  const Icon = meta?.icon;
  const pct = data?.top_client_pct || 0;

  return (
    <section className="bi-surface p-4 space-y-3" data-testid="psi-check">
      <div className="flex items-center gap-2 text-[#FF9500]">
        <Users className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-heading">PSI 80/20 Test</span>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-heading font-extrabold text-lg leading-tight">
          One-client risk check
        </div>
        {data && (
          <span className="text-[11px] text-white/50 shrink-0" data-testid="psi-fy">
            {data.fy_label}
          </span>
        )}
      </div>

      {busy && !data && (
        <div className="text-xs text-white/50 inline-flex items-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Crunching your invoice history…
        </div>
      )}

      {data && (
        <div
          data-testid="psi-verdict"
          className="rounded-xl p-3 border"
          style={{
            borderColor: `${meta.color}55`,
            background: `${meta.color}0d`,
          }}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5" style={{ color: meta.color }} />
            <div className="font-heading font-extrabold text-base" style={{ color: meta.color }}>
              {meta.label}
            </div>
            {data.top_client_pct > 0 && (
              <span className="ml-auto text-lg font-heading font-extrabold tabular-nums" style={{ color: meta.color }}>
                {pct.toFixed(0)}%
              </span>
            )}
          </div>
          <div className="mt-1.5 text-sm text-white/85 leading-snug" data-testid="psi-headline">
            {data.headline}
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full transition-[width]"
              style={{ width: `${Math.min(100, pct)}%`, background: meta.color }}
            />
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-white/40">
            <span>0%</span>
            <span className="text-[#FBBF24]">80% threshold</span>
            <span>100%</span>
          </div>
        </div>
      )}

      {data && data.breakdown && data.breakdown.length > 1 && (
        <div className="space-y-1" data-testid="psi-breakdown">
          <div className="text-[10px] uppercase tracking-[0.2em] font-heading text-white/50">
            Top clients ({data.source === "invoices" ? "by invoices" : "by job budget"})
          </div>
          {data.breakdown.slice(0, 4).map((r) => (
            <div key={r.client} className="flex items-center justify-between gap-2 text-xs py-0.5">
              <div className="min-w-0">
                <div className="truncate font-semibold text-white/90">{r.client}</div>
                <div className="text-[10px] text-white/40">
                  {r.invoice_count} {r.invoice_count === 1 ? "record" : "records"} · {money(r.income)}
                </div>
              </div>
              <div
                className="tabular-nums font-heading font-extrabold shrink-0"
                style={{ color: r.pct >= 80 ? "#F87171" : r.pct >= 50 ? "#FBBF24" : "#34D399" }}
              >
                {r.pct.toFixed(0)}%
              </div>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div className="text-[11px] text-white/50 leading-snug" data-testid="psi-ato-ref">
          {data.ato_reference}
        </div>
      )}

      <TaxDisclaimer compact />
    </section>
  );
}
